/**
 * Client-side freestream → Reynolds & Mach, from the chord plus an operating point given as
 * freestream true airspeed, density altitude and a temperature offset. Used both for the
 * read-out in the Operation pane and to set the RANS run conditions.
 *
 * Atmosphere model: ISA troposphere (valid to ~11 km). The DENSITY altitude fixes the ambient
 * density to its ISA value at that altitude; the temperature is the ISA temperature at that
 * altitude plus `tempOffsetK`, and it (not the density) drives the sound speed and the
 * dynamic viscosity (Sutherland's law). So Re and Mach respond to a hot/cold day independently
 * of the density altitude.
 */

const T0 = 288.15;        // K, sea-level ISA temperature
const LAPSE = 0.0065;     // K/m, tropospheric lapse rate
const RHO0 = 1.225;       // kg/m³, sea-level ISA density
const R_AIR = 287.05;     // J/(kg·K), specific gas constant for air
const G0 = 9.80665;       // m/s², standard gravity
const GAMMA = 1.4;        // ratio of specific heats
const KNOT = 0.514444;    // m/s per knot

// Sutherland's law for air viscosity.
const MU_REF = 1.716e-5, T_MU_REF = 273.15, S_MU = 110.4;

export interface FlowConditions {
  velocity: number;     // m/s
  temperature: number;  // K
  density: number;      // kg/m³
  soundSpeed: number;   // m/s
  viscosity: number;    // Pa·s
  mach: number;
  reynolds: number;     // based on the physical chord
}

const isaTemp = (altM: number): number => T0 - LAPSE * altM;
const isaDensity = (altM: number): number =>
  RHO0 * Math.pow(isaTemp(altM) / T0, G0 / (LAPSE * R_AIR) - 1);
const sutherland = (T: number): number =>
  MU_REF * Math.pow(T / T_MU_REF, 1.5) * ((T_MU_REF + S_MU) / (T + S_MU));

export function flowConditions(
  chordM: number, knots: number, densityAltM: number, tempOffsetK: number,
): FlowConditions {
  const density = isaDensity(densityAltM);
  const temperature = isaTemp(densityAltM) + tempOffsetK;
  const soundSpeed = Math.sqrt(GAMMA * R_AIR * temperature);
  const viscosity = sutherland(temperature);
  const velocity = knots * KNOT;
  return {
    velocity, temperature, density, soundSpeed, viscosity,
    mach: velocity / soundSpeed,
    reynolds: (density * velocity * chordM) / viscosity,
  };
}
